import type { ComponentPropsWithRef } from "react";
import { css, styled } from "styled-components";

import { ChevronIcon, Spinner } from "common/components";
import { useTranslate } from "common/utilities/useTranslate";

type PermissionOptionProps = ComponentPropsWithRef<"select"> & {
  isEditable?: boolean;
  isPending?: boolean;
  value?: "editor" | "viewer";
};

export const PermissionOption = ({
  isEditable: editable = true,
  isPending = false,
  ...rest
}: PermissionOptionProps) => {
  const translate = useTranslate();

  return editable ? (
    <ContainerEditable>
      <Select {...rest}>
        <option value="viewer">{translate("app.create.share_settings.viewer")}</option>
        <option value="editor">{translate("app.create.share_settings.editor")}</option>
      </Select>
      {isPending ? <StyledSpinner /> : <StyledChevron />}
    </ContainerEditable>
  ) : (
    <ContainerReadonly>{rest.defaultValue}</ContainerReadonly>
  );
};

const ContainerReadonly = styled.div`
  color: ${({ theme }) => theme.colors.inputText};
  text-align: right;
  text-transform: capitalize;
`;

const ContainerEditable = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  justify-content: center;
  position: relative;
`;

const Select = styled.select`
  appearance: none;
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.colors.inputText};
  border-radius: 20px;
  color: ${({ theme }) => theme.colors.inputText};
  cursor: pointer;
  font-size: 1rem;
  min-height: 39px;
  outline: none;
  padding: 8px 32px 8px 14px;
  transition: border 0.3s;

  &:disabled {
    cursor: not-allowed;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.inputTextFocused};
  }
`;

const IconStyles = css`
  color: ${({ theme }) => theme.colors.inputText};
  cursor: pointer;
  height: 12px;
  pointer-events: none;
  position: absolute;
  right: 10px;
  width: 12px;
`;

const StyledSpinner = styled(Spinner)`
  ${IconStyles}
`;

const StyledChevron = styled(ChevronIcon)`
  ${IconStyles}
  transform: rotate(90deg);
`;
